import { Alignment, Placement } from '@philips-emr/tws-constants';

import clamp from './clamp';

export interface Rect {
  top: number;
  left: number;
  bottom: number;
  right: number;
  width: number;
  height: number;
}

export interface Position {
  arrow: Record<string, any>;
  guest: Record<string, any>;
  placement: Placement;
  guestRect: Rect;
  hostRect: Rect;
}

const isVertical = (placement: string): boolean => placement === Placement.BOTTOM || placement === Placement.TOP;

function hasColision(rectA: Rect, rectB: Rect, boundary: Rect): boolean {

  const isOutsideBoundary = rectA.top < 0
                         || rectA.left < 0
                         || rectA.right > boundary.width
                         || rectA.bottom > boundary.height;

  const isAboveHost = !(rectA.right <= rectB.left ||
                        rectA.left >= rectB.right ||
                        rectA.bottom <= rectB.top ||
                        rectA.top >= rectB.bottom);

  return isOutsideBoundary || isAboveHost;
}

function align(value: number, hostRect: Rect, alignment: string): number {
  switch (alignment) {
    case Alignment.TOP:
      return hostRect.top;
    case Alignment.BOTTOM:
      return hostRect.top + hostRect.height;
    case Alignment.LEFT:
      return hostRect.left ;
    case Alignment.RIGHT:
      return hostRect.left + hostRect.width;
    default:
      return value;
  }
}

/**
 * @description Returns an array of directions based on the placement
 * orientation. The passed placement must always be the first
 * in the result array.
 * @param {string} placement
 * @returns {array} directions - An array of placements
 *
 */
function getPlacementCycle(placement: Placement): Placement[] {
  const regularDirection = [Placement.TOP, Placement.BOTTOM, Placement.LEFT, Placement.RIGHT];
  const inverseDirection = [Placement.LEFT, Placement.RIGHT, Placement.TOP, Placement.BOTTOM];
  const cycle = isVertical(placement) ? regularDirection : inverseDirection;

  cycle.splice(cycle.indexOf(placement), 1);
  cycle.unshift(placement);

  return cycle;
}

/**
 * @description Process and returns the relative positions for the top placement
 * @param {object} options
 * @param {boolean} [force=true] - Forces the function to return a position even when there's colision
 * @returns {object} position
 *
 */
function getTopPlacement(options: Record<string, any>): object {
  const { guestRect, hostRect, arrowSize, offset, boundary, alignment } = options;

  const bottom = (boundary.bottom - hostRect.top) + arrowSize - offset.top;

  const left = clamp({
    value: hostRect.left + (hostRect.width / 2) - (guestRect.width / 2) + offset.left,
    min: boundary.left,
    max: boundary.right - guestRect.width,
  });

  return { bottom, left: align(left, hostRect, alignment) };
}

/**
 * @description Process and returns the relative positions for the bottom placement
 * @param {object} options
 * @param {boolean} [force=true] - Forces the function to return a position even when there's colision
 * @returns {object} position
 *
 */
function getBottomPlacement(options: Record<string, any>): object {
  const { guestRect, hostRect, arrowSize, boundary, offset, alignment } = options;

  const top = hostRect.top + hostRect.height + arrowSize + offset.top;

  const left = clamp({
    value: hostRect.left + (hostRect.width / 2) - (guestRect.width / 2) + offset.left,
    min: boundary.left,
    max: boundary.right - guestRect.width,
  });

  return { top, left: align(left, hostRect, alignment) };
}

/**
 * @description Process and returns the relative positions for the left placement
 * @param {object} options
 * @param {boolean} [force=true] - Forces the function to return a position even when there's colision
 * @returns {object} position
 *
 */
function getLeftPlacement(options: Record<string, any>): object {
  const { boundary, guestRect, hostRect, arrowSize, offset, alignment } = options;

  const top = clamp({
    value: hostRect.top + (hostRect.height / 2) - (guestRect.height / 2) + offset.top,
    max: boundary.bottom - guestRect.height,
  });

  const left = clamp({
    value: hostRect.left - guestRect.width - arrowSize - offset.left,
    min: boundary.left,
    max: boundary.right - guestRect.width,
  });

  return { left, top: align(top, hostRect, alignment) };
}

/**
 * @description Process and returns the relative positions for the right placement
 * @param {object} options
 * @param {boolean} [force=true] - Forces the function to return a position even when there's colision
 * @returns {object} position
 *
 */
function getRightPlacement(options: Record<string, any>): object {
  const { boundary, guestRect, hostRect, arrowSize, offset, alignment } = options;

  const top = clamp({
    value: hostRect.top + (hostRect.height / 2) - (guestRect.height / 2) + offset.top,
    min: boundary.top,
    max: boundary.bottom - guestRect.height,
  });

  const left = clamp({
    value: hostRect.left + hostRect.width + arrowSize + offset.left,
    min: boundary.left,
    max: boundary.right - guestRect.width,
  });

  return { left, top: align(top, hostRect, alignment) };
}

function getArrowPosition(options: Record<string, any>): object {
  const { placement, hostRect, guestRect, guestPosition, arrowSize } = options;

  if (isVertical(placement)) {
    const left = clamp({
      value: (hostRect.left - guestPosition.left) + (hostRect.width / 2),
      min: arrowSize,
      max: guestRect.width - arrowSize,
    });

    return { left };
  }

  const top = clamp({
    value: (hostRect.top - guestPosition.top) + hostRect.height / 2,
    min: arrowSize,
    max: guestRect.height - arrowSize,
  });

  return { top };
}

function getViewportRect(window: Window): ClientRect {
  const { innerHeight, innerWidth } = window;

  return {
    width: innerWidth,
    height: innerHeight,
    top: 0,
    left: 0,
    right: innerWidth,
    bottom: innerHeight,
  };
}

function getBoundary(boundaryElement: Element, window: Window): ClientRect {
  return boundaryElement
    ? boundaryElement.getBoundingClientRect()
    : getViewportRect(window);
}

/**
 * @description An auxiliar function which returns an object containing the adjusted positioning for element and arrow
 * @param {object} options
 * @param {boolean} [force=false] - Forces the function to return the alignments ignoring viewport and overlaping, default is false
 * @returns {object} position - Object containing guest, arrow and placement
 *
 */
function getPosition(options: Record<string, any>): Position {
  const {
    guestElement,
    hostElement,
    arrowSize = 0,
    placement = Placement.BOTTOM,
    alignment,
    offset = { top: 0, left: 0 },
    boundaryElement,
    window,
  } = options;

  if (!guestElement || !hostElement) {
    return {} as Position;
  }

  const hostRect = hostElement.getBoundingClientRect();
  const guestRect = guestElement.getBoundingClientRect();
  const boundary = getBoundary(boundaryElement, window);

  const placements: { [key in Placement]: (options: Record<string, any>) => object; } = {
    [Placement.TOP]: getTopPlacement,
    [Placement.RIGHT]: getRightPlacement,
    [Placement.BOTTOM]: getBottomPlacement,
    [Placement.LEFT]: getLeftPlacement,
  };

  // @ts-ignore
  const guestPosition = placements[placement]({
    alignment,
    arrowSize,
    guestRect,
    hostRect,
    offset,
    boundary,
  });

  const arrowPosition = getArrowPosition({
    arrowSize,
    guestPosition,
    guestRect,
    hostRect,
    placement,
  });

  return {
    arrow: arrowPosition,
    guest: guestPosition,
    guestRect: getFullRect(placement, guestPosition, guestRect, boundary),
    hostRect: hostRect,
    placement,
  };
}

function getFullRect(placement: string, position: Record<string, any>, guestRect: Rect, boundary: Rect): Rect {
  const { width, height } = guestRect;

  if (placement === Placement.TOP) {
    const { left, bottom } = position;
    const top = boundary.bottom - (bottom + height);
    const right = left + width;
    const rectBottom = top + height;

    return { top, right, bottom: rectBottom, left, width, height };
  }

  const { top, left } = position;
  const bottom = top + height;
  const right = left + width;

  return { top, right, bottom, left, width, height };
}

/**
 * @description Runs through all possible placements and returns the first position without colisions
 * @param {object} options
 * @returns {object} position
 *
 */
function getBestPosition(options: Record<string, any>): Position {
  const { guestElement, hostElement, placement = Placement.BOTTOM, boundaryElement, window } = options;

  if (!guestElement || !hostElement) {
    return {} as Position;
  }

  const placementCycle = getPlacementCycle(placement);
  const boundary = getBoundary(boundaryElement, window);

  for (const placement of placementCycle) {
    const bestPosition = getPosition({ ...options, placement });

    if (!hasColision(bestPosition.guestRect, bestPosition.hostRect, boundary)) {
      return bestPosition;
    }
  }

  return getPosition(options);
}

export default {
  getPosition,
  getBestPosition,
};

export {
  getPosition,
  getBestPosition,
};
